/**
 * UserPrivilegesScreen — ERP Privilege Matrix Editor
 *
 * Layout:
 *   Toolbar : User selector dropdown  ·  Load Profile  ·  Save Privileges
 *   Left    : Module → SubModule → Screen navigation tree
 *   Right   : Privilege matrix — screens in tree model ×
 *             COMMON (View / New / Edit / Delete / Print / Preview)
 *             SPECIAL (Export / Import / Approve / Reject / Cancel / Email / Message / Attach / AI)
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { API_BASE, getToken } from '../../lib/api';
import {
  ChevronDown, ChevronRight, Users, Shield, Save,
  RefreshCw, Copy, CheckSquare, Square, Search, Zap,
} from 'lucide-react';

// ── Operation definitions ────────────────────────────────────────────────────
const COMMON_OPS = [
  { key: 'visible', label: 'View',    short: 'Vw', color: '#6366f1' },
  { key: 'save',    label: 'New',     short: 'Nw', color: '#10b981' },
  { key: 'update',  label: 'Edit',    short: 'Ed', color: '#f59e0b' },
  { key: 'delete',  label: 'Delete',  short: 'Dl', color: '#ef4444' },
  { key: 'print',   label: 'Print',   short: 'Pt', color: '#8b5cf6' },
  { key: 'preview', label: 'Preview', short: 'Pv', color: '#06b6d4' },
];

const SPECIAL_OPS = [
  { key: 'export',  label: 'Export',   short: 'Ex', color: '#0ea5e9' },
  { key: 'import',  label: 'Import',   short: 'Im', color: '#65a30d' },
  { key: 'approve', label: 'Approve',  short: 'Ap', color: '#10b981' },
  { key: 'reject',  label: 'Reject',   short: 'Rj', color: '#f43f5e' },
  { key: 'cancel',  label: 'Cancel',   short: 'Cn', color: '#94a3b8' },
  { key: 'email',   label: 'Email',    short: 'Em', color: '#ec4899' },
  { key: 'message', label: 'Message',  short: 'Ms', color: '#22c55e' },
  { key: 'attach',  label: 'Attach',   short: 'At', color: '#a16207' },
  { key: 'ai',      label: 'AI Chat',  short: 'AI', color: '#a855f7' },
];

const ALL_OPS   = [...COMMON_OPS, ...SPECIAL_OPS];
const ALL_KEYS  = ALL_OPS.map(o => o.key);
const COM_KEYS  = COMMON_OPS.map(o => o.key);
const SPL_KEYS  = SPECIAL_OPS.map(o => o.key);
const DFLT_OPS  = ALL_KEYS.reduce((a, k) => ({ ...a, [k]: false }), {});
const COL_W     = 32; // px per operation column

function authFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
}

// Pure helpers — no hooks
function allEnabled(screens, keys, privMap) {
  return screens.length > 0 && screens.every(s => keys.every(k => privMap[s.screenId]?.[k]));
}
function anyEnabled(screens, keys, privMap) {
  return screens.some(s => keys.some(k => privMap[s.screenId]?.[k]));
}

// ── Apply Profile Button — separate component to avoid ref/closure issues ────
function ApplyProfileButton({ profiles, saving, onApply, isDark, panel, bd, tx, rh, ac, mt }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button data-testid="apply-profile-btn"
        onClick={() => setOpen(p => !p)}
        disabled={saving}
        title="Copy a profile's privileges to this user and save immediately"
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: open ? '#7c3aed' : (isDark ? '#1e1840' : '#eef2ff'), border: `1px solid ${open ? '#7c3aed' : '#6366f1'}`, borderRadius: 6, color: open ? '#fff' : '#6366f1', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, opacity: saving ? .6 : 1, whiteSpace: 'nowrap' }}>
        <Zap size={12} /> Apply Profile →
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 210, background: panel, border: `1px solid #6366f1`, borderRadius: 8, boxShadow: '0 8px 28px rgba(99,102,241,.2)', minWidth: 250, marginTop: 4 }}>
          <div style={{ padding: '6px 12px 4px', fontSize: 10, color: '#6366f1', fontWeight: 800, letterSpacing: '.08em', borderBottom: `1px solid ${bd}` }}>
            APPLY PROFILE DIRECTLY (LOAD + SAVE IN 1 CLICK)
          </div>
          <div style={{ padding: '4px 8px', fontSize: 10, color: mt }}>Select a profile to instantly overwrite this user's privileges:</div>
          {profiles.map(p => (
            <div key={p.ProfileId}
              data-testid={`apply-profile-${p.ProfileId}`}
              onClick={() => { setOpen(false); onApply(p.ProfileId); }}
              style={{ padding: '8px 12px', cursor: 'pointer', color: tx, fontSize: 12, borderTop: `1px solid ${bd}08`, display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = rh; }}
              onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
              <Zap size={11} color="#6366f1" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{p.ProfileName}</div>
                <div style={{ fontSize: 10, color: p.ClientId ? ac : '#10b981' }}>{p.ClientId ? 'Tenant Profile' : 'Global Profile'}</div>
              </div>
            </div>
          ))}
          {!profiles.length && <div style={{ padding: '12px', color: mt, fontSize: 12, textAlign: 'center' }}>No profiles available</div>}
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function UserPrivilegesScreen({ theme }) {
  const isDark = theme === 'dark';

  // ── data ──
  const [users,      setUsers]      = useState([]);
  const [moduleTree, setModuleTree] = useState([]);
  const [privMap,    setPrivMap]    = useState({});
  const [profiles,   setProfiles]   = useState([]);

  // ── UI ──
  const [selectedUser,    setSelectedUser]    = useState(null);
  const [selectedScope,   setSelectedScope]   = useState(null); // null | { type:'module'|'submodule', id }
  const [expandLeft,      setExpandLeft]      = useState({});   // left tree expand
  const [expandWork,      setExpandWork]      = useState({});   // workspace tree expand
  const [saving,          setSaving]          = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [treeLoading,     setTreeLoading]     = useState(true);
  const [userSearch,      setUserSearch]      = useState('');
  const [showUserDrop,    setShowUserDrop]    = useState(false);
  const [showProfilePick, setShowProfilePick] = useState(false);
  const [toast,           setToast]           = useState(null);

  const toastTimer  = useRef(null);
  const userDropRef = useRef(null);
  const profPickRef = useRef(null);

  const notify = useCallback((msg, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Load module tree + users + profiles
  useEffect(() => {
    const load = async () => {
      setTreeLoading(true);
      try {
        const [treeRes, usersRes, profilesRes] = await Promise.all([
          authFetch('/api/uam/module-tree').then(r => r.json()),
          authFetch('/api/uam/users').then(r => r.json()),
          authFetch('/api/uam/profiles').then(r => r.json()),
        ]);
        if (treeRes.success && treeRes.data?.length) {
          const tree = treeRes.data;
          setModuleTree(tree);
          setExpandLeft({ [tree[0].moduleId]: true });
          setExpandWork({ [tree[0].moduleId]: true });
        }
        if (usersRes.success) setUsers(usersRes.data || []);
        if (profilesRes.success) setProfiles(profilesRes.data || []);
      } catch { /**/ } finally { setTreeLoading(false); }
    };
    load();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userDropRef.current && !userDropRef.current.contains(e.target)) setShowUserDrop(false);
      if (profPickRef.current && !profPickRef.current.contains(e.target)) setShowProfilePick(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadUserPrivs = useCallback(async (userId) => {
    setLoading(true);
    setPrivMap({});
    try {
      const r = await authFetch(`/api/uam/user-privileges/${userId}`);
      const d = await r.json();
      if (d.success) setPrivMap(d.data || {});
    } finally { setLoading(false); }
  }, []);

  const selectUser = (u) => {
    setSelectedUser(u);
    setShowUserDrop(false);
    setUserSearch('');
    loadUserPrivs(u.UserId);
  };

  // Toggle a single op for one screen
  const toggleOp = useCallback((screenId, opKey) => {
    setPrivMap(prev => {
      const cur   = prev[screenId] || {};
      const isOn  = !cur[opKey];
      const next  = { ...cur, [opKey]: isOn };
      if (opKey === 'visible' && !isOn) return { ...prev, [screenId]: { ...DFLT_OPS } };
      if (opKey !== 'visible' && isOn && !next.visible) next.visible = true;
      return { ...prev, [screenId]: next };
    });
  }, []);

  // Bulk-set a group of ops across multiple screens
  const setGroupForScreens = useCallback((screens, keys, checked) => {
    setPrivMap(prev => {
      const next = { ...prev };
      screens.forEach(s => {
        const updated = { ...(next[s.screenId] || {}) };
        keys.forEach(k => { updated[k] = checked; });
        if (checked && !updated.visible) updated.visible = true;
        if (!checked && keys.includes('visible')) Object.assign(updated, DFLT_OPS);
        next[s.screenId] = updated;
      });
      return next;
    });
  }, []);

  const applyProfile = async (profileId) => {
    if (!selectedUser) { notify('Select a user first', 'error'); return; }
    try {
      const r = await authFetch(`/api/uam/profiles/${profileId}`);
      const d = await r.json();
      if (d.success && d.data?.privMap) {
        setPrivMap(d.data.privMap);
        notify(`Profile "${d.data.ProfileName}" loaded — save to apply`);
      }
    } catch { notify('Failed to load profile', 'error'); }
    setShowProfilePick(false);
  };

  // ── Apply profile directly and immediately save to DB ──────────────────────
  const applyProfileAndSave = async (profileId) => {
    if (!selectedUser) { notify('Select a user first', 'error'); return; }
    setShowProfilePick(false);
    setSaving(true);
    try {
      // 1. Load profile privMap from API
      const pr = await authFetch(`/api/uam/profiles/${profileId}`);
      const pd = await pr.json();
      if (!pd.success || !pd.data?.privMap) { notify(pd.error || 'Failed to load profile', 'error'); return; }
      const profilePrivMap = pd.data.privMap;
      const profileName    = pd.data.ProfileName || profileId;

      // 2. Immediately save to DB (use loaded privMap directly — no stale-state risk)
      const sr = await authFetch(`/api/uam/user-privileges/${selectedUser.UserId}`, {
        method: 'POST', body: JSON.stringify({ privMap: profilePrivMap }),
      });
      const sd = await sr.json();
      if (sd.success) {
        setPrivMap(profilePrivMap);
        notify(`Profile "${profileName}" applied & saved to ${selectedUser.UserName}`);
      } else {
        notify(sd.error || 'Save failed', 'error');
      }
    } catch { notify('Failed to apply profile', 'error'); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (!selectedUser) { notify('Select a user first', 'error'); return; }
    setSaving(true);
    try {
      const r = await authFetch(`/api/uam/user-privileges/${selectedUser.UserId}`, {
        method: 'POST', body: JSON.stringify({ privMap }),
      });
      const d = await r.json();
      if (d.success) notify('Privileges saved successfully');
      else notify(d.error || 'Save failed', 'error');
    } catch { notify('Network error', 'error'); }
    finally { setSaving(false); }
  };

  // Workspace tree filtered by selectedScope
  const workspaceTree = useMemo(() => {
    if (!selectedScope) return moduleTree;
    if (selectedScope.type === 'module') return moduleTree.filter(m => m.moduleId === selectedScope.id);
    return moduleTree
      .map(m => ({ ...m, subModules: m.subModules.filter(sm => sm.subModuleId === selectedScope.id) }))
      .filter(m => m.subModules.length > 0);
  }, [selectedScope, moduleTree]);

  const filteredUsers = useMemo(() =>
    users.filter(u => !userSearch ||
      u.UserName?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.UserId?.toLowerCase().includes(userSearch.toLowerCase())),
    [users, userSearch]);

  // ── Theme ──
  const bg    = isDark ? '#0f1117' : '#f4f6f9';
  const panel = isDark ? '#161b27' : '#ffffff';
  const bd    = isDark ? '#1e2533' : '#e2e8f0';
  const tx    = isDark ? '#e2e8f0' : '#1e293b';
  const mt    = isDark ? '#64748b' : '#94a3b8';
  const rh    = isDark ? '#1a2035' : '#f1f5f9';
  const ac    = '#6366f1';
  const cHd   = '#4f46e5';
  const sHd   = '#d97706';
  const cBg   = isDark ? '#1a1840' : '#eef2ff';
  const sBg   = isDark ? '#1e1808' : '#fffbeb';

  return (
    <div data-testid="user-privileges-screen"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: bg, fontFamily: "'Inter','Segoe UI',sans-serif", fontSize: 13, overflow: 'hidden' }}>

      {/* ══ TOOLBAR ═══════════════════════════════════════════════════════════ */}
      <div style={{ padding: '9px 16px', borderBottom: `1px solid ${bd}`, background: panel, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        <Shield size={15} color={ac} />
        <span style={{ fontWeight: 800, fontSize: 11, color: mt, letterSpacing: '.08em' }}>USER PRIVILEGES</span>

        {/* User dropdown */}
        <div ref={userDropRef} style={{ position: 'relative' }}>
          <button data-testid="user-selector-btn" onClick={() => setShowUserDrop(p => !p)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: selectedUser ? ac : (isDark ? '#1e2533' : '#f1f5f9'), border: `1px solid ${selectedUser ? ac : bd}`, borderRadius: 7, color: selectedUser ? '#fff' : tx, cursor: 'pointer', fontSize: 12, fontWeight: 600, minWidth: 220, maxWidth: 300 }}>
            <Users size={13} />
            <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedUser ? `${selectedUser.UserName} (${selectedUser.UserId})` : 'Select User…'}
            </span>
            <ChevronDown size={12} />
          </button>
          {showUserDrop && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: panel, border: `1px solid ${bd}`, borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,.3)', width: 290, marginTop: 4 }}>
              <div style={{ padding: '8px 10px', borderBottom: `1px solid ${bd}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: isDark ? '#0f1117' : '#f8fafc', borderRadius: 6, padding: '5px 8px', border: `1px solid ${bd}` }}>
                  <Search size={12} color={mt} />
                  <input autoFocus value={userSearch} onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search users…"
                    style={{ background: 'transparent', border: 'none', outline: 'none', color: tx, fontSize: 12, width: '100%' }} />
                </div>
              </div>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {filteredUsers.map(u => (
                  <div key={u.UserId} onClick={() => selectUser(u)}
                    data-testid={`user-opt-${u.UserId}`}
                    style={{ padding: '7px 12px', cursor: 'pointer', color: tx, fontSize: 12, borderBottom: `1px solid ${bd}08` }}
                    onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = rh; }}
                    onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                    <div style={{ fontWeight: 600 }}>{u.UserName}</div>
                    <div style={{ fontSize: 10, color: mt }}>{u.UserId}{u.GroupName ? ` · ${u.GroupName}` : ''}</div>
                  </div>
                ))}
                {!filteredUsers.length && <div style={{ padding: '16px 12px', color: mt, textAlign: 'center', fontSize: 12 }}>No users found</div>}
              </div>
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {selectedUser && (
          <>
            {/* Load Profile (preview mode — loads into editor, requires manual Save) */}
            <div ref={profPickRef} style={{ position: 'relative' }}>
              <button data-testid="load-profile-btn" onClick={() => setShowProfilePick(p => !p)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: isDark ? '#1e2533' : '#f1f5f9', border: `1px solid ${bd}`, borderRadius: 6, color: tx, cursor: 'pointer', fontSize: 12 }}>
                <Copy size={12} /> Load Profile
              </button>
              {showProfilePick && (
                <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 200, background: panel, border: `1px solid ${bd}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.25)', minWidth: 250, marginTop: 4 }}>
                  <div style={{ padding: '6px 12px', fontSize: 10, color: mt, fontWeight: 700, letterSpacing: '.06em', borderBottom: `1px solid ${bd}` }}>LOAD INTO EDITOR (then save)</div>
                  {profiles.map(p => (
                    <div key={p.ProfileId} onClick={() => applyProfile(p.ProfileId)}
                      data-testid={`profile-pick-${p.ProfileId}`}
                      style={{ padding: '8px 12px', cursor: 'pointer', color: tx, fontSize: 12 }}
                      onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = rh; }}
                      onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                      <div style={{ fontWeight: 600 }}>{p.ProfileName}</div>
                      <div style={{ fontSize: 10, color: p.ClientId ? ac : '#10b981' }}>{p.ClientId ? 'Tenant' : 'Global'}</div>
                    </div>
                  ))}
                  {!profiles.length && <div style={{ padding: '12px', color: mt, fontSize: 12, textAlign: 'center' }}>No profiles</div>}
                </div>
              )}
            </div>

            {/* Apply Profile directly → load + save in 1 click */}
            <ApplyProfileButton
              profiles={profiles}
              saving={saving}
              onApply={applyProfileAndSave}
              isDark={isDark}
              panel={panel}
              bd={bd}
              tx={tx}
              rh={rh}
              ac={ac}
              mt={mt}
            />

            {/* Save */}
            <button onClick={handleSave} disabled={saving}
              data-testid="save-privileges-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', background: ac, border: 'none', borderRadius: 6, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, opacity: saving ? .7 : 1 }}>
              {saving ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
              {saving ? 'Saving…' : 'Save Privileges'}
            </button>
          </>
        )}
      </div>

      {/* ══ BODY ══════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── LEFT NAV TREE ────────────────────────────────────────────────── */}
        <aside style={{ width: 230, flexShrink: 0, borderRight: `1px solid ${bd}`, background: panel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '7px 12px', borderBottom: `1px solid ${bd}`, fontSize: 10, color: mt, fontWeight: 800, letterSpacing: '.08em', flexShrink: 0 }}>
            MODULES / SCREENS
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* All scope */}
            <TreeNode label="All Modules" isActive={!selectedScope}
              onClick={() => setSelectedScope(null)} indent={0} bold isDark={isDark} ac={ac} tx={tx} rh={rh} mt={mt} />

            {treeLoading
              ? <div style={{ padding: 16, color: mt, fontSize: 12, textAlign: 'center' }}>Loading…</div>
              : moduleTree.map(mod => {
                const isExp = !!expandLeft[mod.moduleId];
                const isMod = selectedScope?.type === 'module' && selectedScope.id === mod.moduleId;
                return (
                  <div key={mod.moduleId}>
                    <TreeNode
                      label={`${mod.moduleId}. ${mod.moduleName}`}
                      isActive={isMod} isExpanded={isExp} canExpand
                      onExpand={() => setExpandLeft(p => ({ ...p, [mod.moduleId]: !isExp }))}
                      onClick={() => { setExpandLeft(p => ({ ...p, [mod.moduleId]: true })); setSelectedScope({ type: 'module', id: mod.moduleId }); }}
                      indent={0} bold isDark={isDark} ac={ac} tx={tx} rh={rh} mt={mt} />
                    {isExp && mod.subModules.map(sm => {
                      const smExp = expandLeft[sm.subModuleId] !== false;
                      const isSm  = selectedScope?.type === 'submodule' && selectedScope.id === sm.subModuleId;
                      return (
                        <div key={sm.subModuleId}>
                          <TreeNode
                            label={sm.subModuleName}
                            badge={sm.screens.length}
                            isActive={isSm} isExpanded={smExp} canExpand={sm.screens.length > 0}
                            onExpand={() => setExpandLeft(p => ({ ...p, [sm.subModuleId]: !smExp }))}
                            onClick={() => { setExpandLeft(p => ({ ...p, [sm.subModuleId]: true })); setSelectedScope({ type: 'submodule', id: sm.subModuleId }); }}
                            indent={1} isDark={isDark} ac={ac} tx={tx} rh={rh} mt={mt} />
                          {smExp && sm.screens.map(s => (
                            <TreeNode key={s.screenId} label={s.screenTitle || s.screenId}
                              isActive={false} leaf
                              onClick={() => setSelectedScope({ type: 'submodule', id: sm.subModuleId })}
                              indent={2} isDark={isDark} ac={ac} tx={tx} rh={rh} mt={mt} />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            }
          </div>
        </aside>

        {/* ── RIGHT WORKSPACE ──────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedUser ? (
            <CentreMsg icon={<Shield size={48} strokeWidth={1} style={{ opacity: .22, marginBottom: 16 }} />}
              title="Select a User" msg="Choose a user from the dropdown above to configure their module access privileges"
              tx={tx} mt={mt} />
          ) : (loading || treeLoading) ? (
            <CentreMsg icon={<RefreshCw size={30} style={{ opacity: .4, marginBottom: 14, animation: 'spin 1s linear infinite' }} />}
              title="Loading Privileges…" tx={tx} mt={mt} />
          ) : (
            <PrivMatrix
              tree={workspaceTree} privMap={privMap} expandWork={expandWork}
              setExpandWork={setExpandWork} toggleOp={toggleOp} setGroupForScreens={setGroupForScreens}
              isDark={isDark} panel={panel} bd={bd} tx={tx} mt={mt} rh={rh}
              cHd={cHd} sHd={sHd} cBg={cBg} sBg={sBg} />
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, background: toast.type === 'error' ? '#ef4444' : '#10b981', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,.3)', animation: 'fadeIn .2s ease' }}>
          {toast.msg}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TreeNode({ label, badge, isActive, isExpanded, canExpand, onExpand, onClick, indent, bold, leaf, isDark, ac, tx, rh, mt }) {
  const pad = 12 + indent * 16;
  return (
    <div onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', padding: `${leaf ? 3 : 6}px 10px ${leaf ? 3 : 6}px ${pad}px`, cursor: 'pointer', borderLeft: isActive ? `3px solid ${ac}` : '3px solid transparent', background: isActive ? (isDark ? '#1a2035' : '#eef2ff') : 'transparent', transition: 'background .1s' }}
      onMouseEnter={e => { if (!e.currentTarget) return; if (!isActive) e.currentTarget.style.background = rh; }}
      onMouseLeave={e => { if (!e.currentTarget) return; if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
      {canExpand
        ? <span style={{ width: 16, flexShrink: 0, color: mt }} onClick={e => { e.stopPropagation(); onExpand && onExpand(); }}>
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        : <span style={{ width: 16, flexShrink: 0 }} />}
      <span style={{ fontSize: leaf ? 10 : 11, fontWeight: bold ? 700 : leaf ? 400 : 500, color: isActive ? ac : (leaf ? mt : tx), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {badge != null && <span style={{ fontSize: 9, color: mt, marginLeft: 4, flexShrink: 0 }}>{badge}</span>}
    </div>
  );
}

function CentreMsg({ icon, title, msg, tx, mt }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      {icon}
      {title && <div style={{ fontSize: 15, fontWeight: 600, color: tx, marginBottom: 6 }}>{title}</div>}
      {msg && <div style={{ fontSize: 13, color: mt, textAlign: 'center', maxWidth: 340 }}>{msg}</div>}
    </div>
  );
}

function GrpBtn({ label, checked, partial, color, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 7px', background: checked ? `${color}22` : 'transparent', border: `1px solid ${checked || partial ? color : '#e2e8f0'}`, borderRadius: 4, cursor: 'pointer', fontSize: 9, fontWeight: 700, color: checked || partial ? color : '#94a3b8' }}>
      {checked ? <CheckSquare size={9} /> : partial ? <CheckSquare size={9} style={{ opacity: .55 }} /> : <Square size={9} />}
      {label}
    </button>
  );
}

function MiniBtn({ label, title, checked, partial, color, onClick }) {
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '1px 5px', background: 'none', border: `1px solid ${checked ? color : '#cbd5e1'}`, borderRadius: 3, cursor: 'pointer', fontSize: 9, color: checked ? color : '#94a3b8' }}>
      {checked ? <CheckSquare size={9} /> : partial ? <CheckSquare size={9} style={{ opacity: .5 }} /> : <Square size={9} />}
      {label}
    </button>
  );
}

// ── Privilege Matrix workspace ────────────────────────────────────────────────
function PrivMatrix({ tree, privMap, expandWork, setExpandWork, toggleOp, setGroupForScreens, isDark, panel, bd, tx, mt, rh, cHd, sHd, cBg, sBg }) {
  const TOTAL_W = ALL_OPS.length * COL_W + 2;
  const getOp   = (sid, k) => privMap[sid]?.[k] ?? false;

  // All visible screens across the full tree (for column-header toggle)
  const allVisibleScreens = tree.flatMap(m => m.subModules.flatMap(sm => sm.screens));

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>

      {/* Sticky column header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: panel, borderBottom: `2px solid ${bd}` }}>
        {/* Group labels row — with global ALL / NONE buttons */}
        <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${bd}` }}>
          <div style={{ width: 280, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, padding: '0 10px' }}>
            <span style={{ fontSize: 9, color: mt, fontWeight: 700, letterSpacing: '.06em' }}>ALL SCREENS:</span>
            <GrpBtn label="All" checked={allEnabled(allVisibleScreens, ALL_KEYS, privMap)} partial={anyEnabled(allVisibleScreens, ALL_KEYS, privMap) && !allEnabled(allVisibleScreens, ALL_KEYS, privMap)} color="#6366f1"
              onClick={() => setGroupForScreens(allVisibleScreens, ALL_KEYS, !allEnabled(allVisibleScreens, ALL_KEYS, privMap))} />
            <GrpBtn label="Common" checked={allEnabled(allVisibleScreens, COM_KEYS, privMap)} partial={anyEnabled(allVisibleScreens, COM_KEYS, privMap) && !allEnabled(allVisibleScreens, COM_KEYS, privMap)} color={cHd}
              onClick={() => setGroupForScreens(allVisibleScreens, COM_KEYS, !allEnabled(allVisibleScreens, COM_KEYS, privMap))} />
            <GrpBtn label="Special" checked={allEnabled(allVisibleScreens, SPL_KEYS, privMap)} partial={anyEnabled(allVisibleScreens, SPL_KEYS, privMap) && !allEnabled(allVisibleScreens, SPL_KEYS, privMap)} color={sHd}
              onClick={() => setGroupForScreens(allVisibleScreens, SPL_KEYS, !allEnabled(allVisibleScreens, SPL_KEYS, privMap))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: cBg, width: COMMON_OPS.length * COL_W, fontSize: 9, fontWeight: 800, color: cHd, letterSpacing: '.12em', padding: '3px 0', borderLeft: `1px solid ${bd}` }}>
            COMMON
          </div>
          <div style={{ width: 2, background: bd, flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: sBg, width: SPECIAL_OPS.length * COL_W, fontSize: 9, fontWeight: 800, color: sHd, letterSpacing: '.12em', padding: '3px 0' }}>
            SPECIAL
          </div>
        </div>
        {/* Column codes — vertical text + clickable to toggle that op for ALL screens */}
        <div style={{ display: 'flex', alignItems: 'flex-end', background: isDark ? '#1e2533' : '#f8fafc', paddingBottom: 4 }}>
          <div style={{ width: 200, flexShrink: 0, padding: '0 12px', fontSize: 10, fontWeight: 700, color: mt, letterSpacing: '.06em', alignSelf: 'center' }}>SCREEN</div>
          <div style={{ width: 80, flexShrink: 0, textAlign: 'center', fontSize: 9, color: mt, alignSelf: 'center', lineHeight: 1.2 }}>
            <div>ID</div>
            <div style={{ fontSize: 8, color: mt, opacity: .7 }}>click name = all ops</div>
          </div>
          {COMMON_OPS.map(op => {
            const colAll = allVisibleScreens.length > 0 && allVisibleScreens.every(s => privMap[s.screenId]?.[op.key]);
            return (
              <div key={op.key}
                title={`${op.label} — click to toggle ALL screens`}
                onClick={() => setGroupForScreens(allVisibleScreens, [op.key], !colAll)}
                style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 2, minHeight: 56, cursor: 'pointer', borderRadius: 3, transition: 'background .12s' }}
                onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = `${op.color}18`; }}
                onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 12, fontWeight: 800, color: colAll ? op.color : `${op.color}88`, userSelect: 'none', letterSpacing: '.03em' }}>{op.label}</span>
              </div>
            );
          })}
          <div style={{ width: 2, background: bd, alignSelf: 'stretch', flexShrink: 0 }} />
          {SPECIAL_OPS.map(op => {
            const colAll = allVisibleScreens.length > 0 && allVisibleScreens.every(s => privMap[s.screenId]?.[op.key]);
            return (
              <div key={op.key}
                title={`${op.label} — click to toggle ALL screens`}
                onClick={() => setGroupForScreens(allVisibleScreens, [op.key], !colAll)}
                style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 2, minHeight: 56, cursor: 'pointer', borderRadius: 3, transition: 'background .12s' }}
                onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = `${op.color}18`; }}
                onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 12, fontWeight: 800, color: colAll ? op.color : `${op.color}88`, userSelect: 'none', letterSpacing: '.03em' }}>{op.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tree body */}
      {tree.length === 0
        ? <div style={{ padding: '40px 20px', color: mt, textAlign: 'center', fontSize: 13 }}>No screens found for this selection</div>
        : tree.map(mod => {
          const modScreens = mod.subModules.flatMap(sm => sm.screens);
          const modExp  = expandWork[mod.moduleId] !== false;
          const mAllC   = allEnabled(modScreens, COM_KEYS, privMap);
          const mAnyC   = anyEnabled(modScreens, COM_KEYS, privMap);
          const mAllS   = allEnabled(modScreens, SPL_KEYS, privMap);
          const mAnyS   = anyEnabled(modScreens, SPL_KEYS, privMap);

          return (
            <div key={mod.moduleId}>
              {/* Module row */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', background: isDark ? '#1e2533' : '#e8edf5', borderBottom: `1px solid ${bd}`, cursor: 'pointer', minHeight: 32 }}
                onClick={() => setExpandWork(p => ({ ...p, [mod.moduleId]: !modExp }))}>
                <span style={{ width: 18, color: mt, flexShrink: 0 }}>{modExp ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
                <span style={{ fontWeight: 800, fontSize: 12, color: tx, flex: 1 }}>{mod.moduleId}. {mod.moduleName}</span>
                <div style={{ display: 'flex', gap: 5, marginRight: 8 }}>
                  <GrpBtn label="Common" checked={mAllC} partial={mAnyC && !mAllC} color={cHd}
                    onClick={e => { e.stopPropagation(); setGroupForScreens(modScreens, COM_KEYS, !mAllC); }} />
                  <GrpBtn label="Special" checked={mAllS} partial={mAnyS && !mAllS} color={sHd}
                    onClick={e => { e.stopPropagation(); setGroupForScreens(modScreens, SPL_KEYS, !mAllS); }} />
                </div>
                <div style={{ width: TOTAL_W, flexShrink: 0 }} />
              </div>

              {modExp && mod.subModules.map(sm => {
                const smExp  = expandWork[sm.subModuleId] !== false;
                const sAllC  = allEnabled(sm.screens, COM_KEYS, privMap);
                const sAnyC  = anyEnabled(sm.screens, COM_KEYS, privMap);
                const sAllS  = allEnabled(sm.screens, SPL_KEYS, privMap);
                const sAnyS  = anyEnabled(sm.screens, SPL_KEYS, privMap);
                return (
                  <div key={sm.subModuleId}>
                    {/* SubModule row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 12px 4px 28px', background: isDark ? '#161b27' : '#f0f4ff', borderBottom: `1px solid ${bd}`, cursor: 'pointer', minHeight: 28 }}
                      onClick={() => setExpandWork(p => ({ ...p, [sm.subModuleId]: !smExp }))}>
                      <span style={{ width: 14, color: mt, flexShrink: 0 }}>{smExp ? <ChevronDown size={11} /> : <ChevronRight size={11} />}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: mt, flex: 1 }}>{sm.subModuleName}</span>
                      <div style={{ display: 'flex', gap: 4, marginRight: 8 }}>
                        <MiniBtn checked={sAllC} partial={sAnyC && !sAllC} color={cHd} label="All" title="All Common"
                          onClick={e => { e.stopPropagation(); setGroupForScreens(sm.screens, COM_KEYS, !sAllC); }} />
                        <MiniBtn checked={sAllS} partial={sAnyS && !sAllS} color={sHd} label="Spl" title="All Special"
                          onClick={e => { e.stopPropagation(); setGroupForScreens(sm.screens, SPL_KEYS, !sAllS); }} />
                      </div>
                      <div style={{ width: TOTAL_W, flexShrink: 0 }} />
                    </div>

                    {/* Screen rows */}
                    {smExp && sm.screens.map(s => {
                      const sAllOps = ALL_KEYS.every(k => getOp(s.screenId, k));
                      const sAnyOps = ALL_KEYS.some(k => getOp(s.screenId, k));
                      return (
                      <div key={s.screenId}
                        style={{ display: 'flex', alignItems: 'center', padding: '2px 12px 2px 44px', borderBottom: `1px solid ${bd}08`, minHeight: 27 }}
                        onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = rh; }}
                        onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                        {/* Screen name — click to toggle ALL ops for this screen */}
                        <div
                          onClick={() => setGroupForScreens([s], ALL_KEYS, !sAllOps)}
                          title={`Click to ${sAllOps ? 'remove all' : 'grant all'} permissions for this screen`}
                          style={{ width: 156, flexShrink: 0, fontSize: 11, color: sAllOps ? '#6366f1' : sAnyOps ? tx : mt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', fontWeight: sAllOps ? 700 : sAnyOps ? 500 : 400, textDecoration: 'underline', textDecorationColor: 'transparent', textUnderlineOffset: 2, transition: 'color .12s' }}
                          onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.textDecorationColor = '#6366f1'; }}
                          onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.color = sAllOps ? '#6366f1' : sAnyOps ? tx : mt; e.currentTarget.style.textDecorationColor = 'transparent'; }}>
                          {s.screenTitle || s.screenId}
                        </div>
                        <div style={{ width: 80, flexShrink: 0, textAlign: 'center', fontSize: 10, color: mt }}>{s.screenId}</div>
                        {/* COMMON checkboxes */}
                        {COMMON_OPS.map(op => (
                          <div key={op.key} style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <input type="checkbox"
                              data-testid={`priv-${s.screenId}-${op.key}`}
                              checked={getOp(s.screenId, op.key)}
                              onChange={() => toggleOp(s.screenId, op.key)}
                              title={op.label}
                              style={{ accentColor: op.color, width: 13, height: 13, cursor: 'pointer' }}
                            />
                          </div>
                        ))}
                        {/* Separator */}
                        <div style={{ width: 2, background: isDark ? '#2d3748' : '#e2e8f0', alignSelf: 'stretch', flexShrink: 0 }} />
                        {/* SPECIAL checkboxes */}
                        {SPECIAL_OPS.map(op => (
                          <div key={op.key} style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <input type="checkbox"
                              data-testid={`priv-${s.screenId}-${op.key}`}
                              checked={getOp(s.screenId, op.key)}
                              onChange={() => toggleOp(s.screenId, op.key)}
                              title={op.label}
                              style={{ accentColor: op.color, width: 13, height: 13, cursor: 'pointer' }}
                            />
                          </div>
                        ))}
                      </div>
                      );
                    })}
                    {smExp && !sm.screens.length && (
                      <div style={{ padding: '5px 12px 5px 44px', color: mt, fontSize: 10, fontStyle: 'italic' }}>No screens</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })
      }
    </div>
  );
}
